import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal } from "./Modal";
import { z } from "zod/v4";
import { Button } from "../ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { ChevronDownIcon } from "lucide-react";
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { useUpsertProperty } from "@/hooks/queryHooks";
import type { PropertyPayload } from "global-types";
import { 
  createNumberSchema,
  createDateSchema,
  createYearSchema,
  createLatitudeSchema,
  createLongitudeSchema,
  createUrlSchema,
  createUuidSchema,
  createStringSchema
} from "@/lib/utils";

const formSchema = z.object<Record<keyof Omit<PropertyPayload, "id">, any>>({
  property_name: z.string(),
  property_identifier: z.string(),
  total_units: createNumberSchema(),
  year_built: createYearSchema(),
  deal_start_date: createDateSchema(),
  deal_sold_date: createDateSchema(),
  purchase_price: createNumberSchema(),
  selling_price: createNumberSchema(),
  portfolio_id: z.uuid(),
  image_url: createUrlSchema(),
  active_tenants: createNumberSchema(),
  average_rent: createNumberSchema(),
  lat: createLatitudeSchema(),
  lon: createLongitudeSchema(),
  monthly_revenue: createNumberSchema(),
  occupancy_rate: createNumberSchema(),
  address: createStringSchema(),
  city: createStringSchema(),
  state: createStringSchema(),
  zip_code: createStringSchema(),
  hello_data_property_id: createUuidSchema(),
  property_management_system: createStringSchema(),
});

export const NewPropertyModal = () => {
  const { closeModal, getModalState } = useModalNavigation();
  const modalState = getModalState();
  const portfoliosFromState = modalState?.portfolios || [];
  const createPropertyMutation = useUpsertProperty();
  const { error: mutationError, isPending } = createPropertyMutation;
  const [error, setError] = useState<string | null>(null);
  const [isOptionalFieldsOpen, setIsOptionalFieldsOpen] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      portfolio_id: portfoliosFromState[0]?.id || "",
    },
  });

  const { getValues, setValue } = form;

  const handleCancel = () => {
    closeModal();
    form.reset();
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      await createPropertyMutation.mutateAsync(values);
      closeModal();
      form.reset();
    } catch (err) {
      console.error("Failed to create portfolio:", err);
    }
  };

  useEffect(() => {
    if (mutationError) {
      setError(mutationError.message);
    }
  }, [mutationError]);

  useEffect(() => {
    if (portfoliosFromState.length > 0 && !form.getValues("portfolio_id")) {
      form.setValue("portfolio_id", portfoliosFromState[0].id);
    }
  }, [portfoliosFromState, form]);

  return (
    <Modal
      className="overflow-y-scroll max-h-full"
      title="Create New Property"
      description="Fill out the information below to create a new property in the system."
      onClose={handleCancel}
    >
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
          {error}
        </div>
      )}
      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="md:grid md:grid-cols-2 md:gap-4">
            <FormField
              control={form.control}
              name="property_name"
              render={({ field }) => (
                <FormItem aria-required={true}>
                  <FormLabel>Property Name *</FormLabel>
                  <FormControl>
                    <Input required {...field} onChange={(e) => {
                      setValue("property_name", e.target.value)
                      setValue("property_identifier", e.target.value)
                    }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="property_identifier"
              render={({ field }) => (
                <FormItem aria-required={true}>
                  <FormLabel>Property Identifier *</FormLabel>
                  <FormControl>
                    <Input
                      required
                      {...field}
                      onBlur={(e) => {
                        !e.target.value.trim() && setValue("property_identifier", getValues("property_name"))
                      }}
                      onFocus={(e) => {
                        !e.target.value.trim() && setValue("property_identifier", "")
                      }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="portfolio_id"
              render={({ field }) => (
                <FormItem className="md:col-span-2" aria-required={true}>
                  <FormLabel>Select Portfolio *</FormLabel>
                  <FormControl>
                    <Select
                      required
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a Portfolio" />
                      </SelectTrigger>
                      <SelectContent>
                        {portfoliosFromState.map(
                          ({ id, name }: { id: string; name: string }) => (
                            <SelectItem key={id} value={id}>
                              {name}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <Collapsible
            open={isOptionalFieldsOpen}
            onOpenChange={setIsOptionalFieldsOpen}
            className="w-full"
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full text-base font-medium py-4 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md">
              Optional Fields
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform duration-200 ${isOptionalFieldsOpen ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-4">
              <div className="md:grid md:grid-cols-2 md:gap-4 space-y-4 md:space-y-0">
                <FormField
                  control={form.control}
                  name="total_units"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Total Units</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="year_built"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Year Built</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="deal_start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deal Start Date</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="YYYY-MM-DD"
                          type="date"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="deal_sold_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deal End Date</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="YYYY-MM-DD"
                          type="date"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="purchase_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Purchase Price</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="selling_price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Selling Price</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="image_url"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Image URL</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="active_tenants"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Active Tenants</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="average_rent"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Average Rent</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Latitude</FormLabel>
                      <FormControl>
                        <Input type="integer" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lon"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Longitude</FormLabel>
                      <FormControl>
                        <Input type="integer" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="monthly_revenue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Monthly Revenue</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="occupancy_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Occupancy Rate</FormLabel>
                      <FormControl>
                        <Input type="number" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="zip_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Zip Code</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="hello_data_property_id"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Hello Data Property ID</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
          <div className="flex justify-end space-x-2 pt-4 md:col-span-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              className="bg-lime text-foreground cursor-pointer hover:text-background"
              type="submit"
              disabled={isPending}
            >
              {isPending ? "Creating..." : "Create Property"}
            </Button>
          </div>
        </form>
      </Form>
    </Modal>
  );
};
